import { Link, type LinkProps } from '@tanstack/react-router';

import { AppIcons, type AppIconName } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

interface SidebarNavLinkProps {
  icon: AppIconName;
  label: string;
  to: LinkProps['to'];
  params?: LinkProps['params'];
  isActive?: boolean;
}

/**
 * SidebarNavLink — one row inside a `SidebarSection`. Renders a
 * router-aware link with leading icon, label, and `aria-current="page"`
 * + brand tint when active. Hover state lifts the muted background.
 * Visual decisions live here so settings/profile sidebars never
 * duplicate the class string.
 */
export function SidebarNavLink({
  icon,
  label,
  to,
  params,
  isActive = false,
}: Readonly<SidebarNavLinkProps>): React.JSX.Element {
  return (
    <Link
      to={to as never}
      params={params as never}
      aria-current={isActive ? 'page' : undefined}
      className={twcn(
        'flex h-row-h items-center gap-2 rounded-sm bg-transparent px-2 text-meta text-foreground-default no-underline transition-colors duration-fast ease-standard [&>svg]:flex-shrink-0 [&>svg]:text-muted-foreground',
        isActive
          ? 'bg-primary-tint font-medium text-primary-ink shadow-inset-primary-soft [&>svg]:text-primary'
          : 'hover:bg-accent',
      )}
    >
      <AppIcons name={icon} size={14} />
      <span className="flex-1 truncate">{label}</span>
    </Link>
  );
}
